import {Plural, Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC, useCallback, useMemo} from 'react';

import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {useLinkNavigation} from '../../components/common/navigation/useLinkNavigation';
import {buildCustomSnoozeFromDynamicPreset} from '../../helpers/models/snoozePresetHelpers';
import {useAppDispatch, useAppSelector} from '../../helpers/reduxHelpers';
import {SettingsCustomSnoozeModel} from '../../models/api/customSnoozeModel';
import {RRelativeTime} from '../../models/api/preferencesModel';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {SettingsResourcePagesEnum, SettingsSnoozesUrl} from '../../models/url/settingsUrlModel';
import {ResourceMap} from '../../store/appTypes';
import {
  createCustomSnoozeAction,
  deleteBulkCustomSnoozeAction,
  deleteCustomSnoozeAction,
  saveCustomSnoozeAction,
} from '../../store/data/preferences/preferencesActions';
import {getCustomSnoozes, getReminderPresets} from '../../store/data/preferences/preferencesSelectors';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {SettingsSnoozeForm} from './settingsSnoozesForm';
import {SnoozesError} from './settingsSnoozesHelpers';
import {SettingsSnoozesList} from './settingsSnoozesList';

/*
 * Props.
 */

interface SettingsSnoozesContainerProps {
  panelId: SettingsPanelId;
  url: SettingsSnoozesUrl;
}

/*
 * Component.
 */

export const SettingsSnoozesContainer: FC<SettingsSnoozesContainerProps> = (props) => {
  const customSnoozes = useAppSelector(getCustomSnoozes);
  const reminderPresets = useAppSelector(getReminderPresets);
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const {resourcePage} = props.url;
  const {showBanner} = useSettingsBanners();
  const {navigateToLink} = useLinkNavigation();

  const buildInitialSnoozes = useCallback(() => {
    if (customSnoozes === null) {
      return _(reminderPresets)
        .toPairs()
        .map(
          (pair) =>
            RRelativeTime.is(pair[1]) && buildCustomSnoozeFromDynamicPreset(pair[1], pair[0], localize),
        )
        .compact()
        .value();
    }

    return customSnoozes;
  }, [customSnoozes, localize, reminderPresets]);

  const snoozesMap: ResourceMap<SettingsCustomSnoozeModel> = useMemo(
    () =>
      _(buildInitialSnoozes())
        .map((s, index) => ({id: index + 1, snooze: s}))
        .keyBy((s) => s.id)
        .mapValues((s) => ({
          id: s.id,
          ...s.snooze,
        }))
        .value(),
    [buildInitialSnoozes],
  );
  const lastSnoozeId = useMemo(
    () =>
      _(buildInitialSnoozes())
        .map((_s, index) => index + 1)
        .last() || 1,
    [buildInitialSnoozes],
  );

  const editedSnooze = props.url.snoozeId !== undefined && snoozesMap[props.url.snoozeId];

  const onSaveClick = async (snooze: SettingsCustomSnoozeModel) => {
    try {
      if (editedSnooze) {
        await dispatch(saveCustomSnoozeAction(snooze));

        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="settings-snoozes-update-success">
              Snooze &quot;{snooze.name}&quot; has been updated
            </Trans>
          ),
        });
        const settingsOverviewUrl = buildSettingsPath({
          ...props.url,
          resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          snoozeId: undefined,
        });
        navigateToLink(settingsOverviewUrl);
      } else {
        await dispatch(createCustomSnoozeAction(snooze));

        showBanner({
          type: SettingsBannersEnum.SUCCESS,
          message: (
            <Trans id="settings-snoozes-create-success">
              Snooze &quot;{snooze.name}&quot; has been created
            </Trans>
          ),
        });
        const settingsOverviewUrl = buildSettingsPath({
          ...props.url,
          resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          snoozeId: undefined,
        });
        navigateToLink(settingsOverviewUrl);
      }
    } catch (err) {
      if (err instanceof SnoozesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  const onSingleDeleteClick = async (snoozeToDelete: SettingsCustomSnoozeModel) => {
    try {
      await dispatch(deleteCustomSnoozeAction(snoozeToDelete));

      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: (
          <Trans id="settings-snoozes-delete-success">
            Snooze &quot;{snoozeToDelete.name}&quot; has been deleted
          </Trans>
        ),
      });
      const settingsOverviewUrl = buildSettingsPath({
        ...props.url,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
        snoozeId: undefined,
      });
      navigateToLink(settingsOverviewUrl);
    } catch (err) {
      if (err instanceof SnoozesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  const onBulkDeleteClick = async (snoozeIds: ReadonlyArray<number>) => {
    try {
      await dispatch(deleteBulkCustomSnoozeAction(snoozeIds));

      showBanner({
        type: SettingsBannersEnum.SUCCESS,
        message: (
          <Plural
            id="settings-snoozes-deletemany-success"
            value={snoozeIds.length}
            one="Deleted 1 snooze"
            other="Deleted # snoozes"
          />
        ),
      });
      const settingsOverviewUrl = buildSettingsPath({
        ...props.url,
        resourcePage: SettingsResourcePagesEnum.OVERVIEW,
        snoozeId: undefined,
      });
      navigateToLink(settingsOverviewUrl);
    } catch (err) {
      if (err instanceof SnoozesError) {
        showBanner({
          type: SettingsBannersEnum.ERROR,
          message: err.message,
        });
      }
    }
  };

  if (resourcePage === SettingsResourcePagesEnum.EDIT && editedSnooze) {
    return (
      <SettingsSnoozeForm
        lastSnoozeId={lastSnoozeId}
        panelId={props.panelId}
        snoozesUrl={props.url}
        snooze={editedSnooze}
        onDeleteClick={onSingleDeleteClick}
        onSaveClick={onSaveClick}
        isEditing
      />
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW) {
    return (
      <SettingsSnoozeForm
        lastSnoozeId={lastSnoozeId}
        panelId={props.panelId}
        snoozesUrl={props.url}
        onDeleteClick={onSingleDeleteClick}
        onSaveClick={onSaveClick}
      />
    );
  }

  return (
    <SettingsSnoozesList
      snoozes={snoozesMap}
      snoozesUrl={props.url}
      onSingleDeleteClick={onSingleDeleteClick}
      onBulkDeleteClick={onBulkDeleteClick}
    />
  );
};
